import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import clinicAPI from "../services/clinicApi";

const ClientAthleteContext = createContext(null);

export function ClientAthleteProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [portalContext, setPortalContext] = useState(null);
  const [activeAthleteId, setActiveAthleteId] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await clinicAPI.getMyPortalContext();
      setPortalContext(data);
      setActiveAthleteId((prev) => {
        const allowed = new Set((data.athletes || []).map((a) => String(a.id)));
        if (prev && allowed.has(String(prev))) return prev;
        return data.default_athlete_id || data.athletes?.[0]?.id || null;
      });
    } catch {
      setPortalContext({ mode: "self", athletes: [], default_athlete_id: null });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const athletes = portalContext?.athletes || [];
  const isParentMode = portalContext?.mode === "parent" && athletes.length > 0;
  const activeAthlete =
    athletes.find((a) => String(a.id) === String(activeAthleteId)) || athletes[0] || null;

  const value = useMemo(
    () => ({
      loading,
      portalContext,
      athletes,
      isParentMode,
      activeAthleteId: activeAthlete?.id || null,
      activeAthlete,
      setActiveAthleteId,
      reloadPortalContext: load,
      athleteQueryParam: activeAthlete?.id ? { athlete_id: activeAthlete.id } : {},
    }),
    [loading, portalContext, athletes, isParentMode, activeAthlete, load],
  );

  return (
    <ClientAthleteContext.Provider value={value}>
      {children}
    </ClientAthleteContext.Provider>
  );
}

export function useClientAthlete() {
  const ctx = useContext(ClientAthleteContext);
  if (!ctx) {
    return {
      loading: false,
      portalContext: null,
      athletes: [],
      isParentMode: false,
      activeAthleteId: null,
      activeAthlete: null,
      setActiveAthleteId: () => {},
      reloadPortalContext: async () => {},
      athleteQueryParam: {},
    };
  }
  return ctx;
}
